/**
 * errorMessages — turns any API/network failure into words a normal person
 * can act on.
 *
 * Backend messages are written for developers ("Validation failed: ...",
 * "Failed to create archive post"). They are useful in logs and useless to a
 * creator on a phone. This module deliberately ignores the backend's wording
 * and answers two questions instead: what went wrong, and what do I do now.
 *
 * Codes come from `apiErrors.ERR`; copy lives here so the two can change
 * independently.
 */
import { ERR } from './apiErrors';
import type { ApiErrorResponse } from '../services/apiClient';

/**
 * Candidate codes for an error, most specific first.
 *
 * The backend sends both: `appCode` is the precise business reason
 * (STORAGE_UNAVAILABLE), `code` the HTTP status (503). We try the specific one
 * and fall back to the status, so a new backend code we have no copy for still
 * produces a sensible message instead of the generic fallback.
 */
const codesOf = (e: unknown): (string | number)[] => {
  if (!e || typeof e !== 'object') return [];
  const asApi = e as Partial<ApiErrorResponse>;
  if (asApi.error) {
    return [asApi.error.appCode, asApi.error.code].filter(
      (c): c is string | number => c !== undefined && c !== null,
    );
  }
  const asErr = e as { code?: string | number };
  return asErr.code === undefined || asErr.code === null ? [] : [asErr.code];
};

/**
 * Plain-English copy per failure. Every line says what happened and what to
 * try next — never "an error occurred".
 */
const MESSAGES: Record<string, string> = {
  [ERR.NETWORK_OFFLINE]:
    "You're offline right now. Check your internet and try again — nothing was lost.",
  [ERR.NETWORK_TIMEOUT]:
    'Your internet is too slow to finish this, so it stopped partway. Try again on Wi-Fi, or use fewer photos.',
  [ERR.REQUEST_CANCELLED]: 'Stopped. Nothing was saved.',
  [ERR.BUYER_FORBIDDEN]:
    "You're on a buyer account, so you can't post. Switch to a creator account to share your work.",

  401: 'You were signed out. Please sign in again, then try once more.',
  403: "You don't have permission to do this.",
  404: "We couldn't find that. It may have been deleted.",
  409: 'That was already saved. Refresh the page to see it.',
  413: 'One of your photos is too big. Remove it or pick a smaller one, then try again.',
  415: "One of your photos is in a format we can't read. Please use a JPG or PNG.",
  429: "You've tried a few times too quickly. Wait a minute, then try again.",
  500: 'Something broke on our side, not yours. Please try again in a moment.',
  502: 'Our server is busy. Please try again in a moment.',
  503: 'Our server is busy. Please try again in a moment.',
  504: 'Our server took too long to answer. Please try again in a moment.',
};

/**
 * Copy for a code we already know, e.g. when the UI decides the reason itself
 * (an upload the client gave up on) rather than reading it off a response.
 */
export const friendlyMessageForCode = (
  code: string | number | undefined | null,
  fallback: string,
): string => {
  if (code === undefined || code === null) return fallback;
  return MESSAGES[String(code)] ?? fallback;
};

/**
 * Best user-facing sentence for an error.
 *
 * @param fallback what to say when we can't identify the cause. Write it for
 *   the specific action, e.g. "We couldn't create your post. Please try again."
 */
export const friendlyErrorMessage = (e: unknown, fallback: string): string => {
  for (const code of codesOf(e)) {
    const message = MESSAGES[String(code)];
    if (message) return message;
  }
  return fallback;
};
